import React, {useEffect, useRef, useState} from "react";
import {Alert, AutoComplete, Button, Col, Drawer, Form, Input, Modal, Popconfirm, Row, Select, Space, Spin, Upload, message} from "antd";
import {DeleteOutlined, EnvironmentOutlined, FileOutlined, UploadOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {Link} from "react-router-dom";
import {ApiComplexesService, ApiCuratorService} from "../../../../../shared/api";
import {ApiApartmentBuilderService} from "../../../../../shared/api/apartment/builder/builder.service";
import {ApiContentService} from "../../../../../shared/api/content/content.service";
import {config} from "../../../../../shared/config/config";
import {authModel} from "../../../../../entities/auth";

// The ЖК card: the fields the agents see on the complex page. Saves only what was changed; photos and documents
// upload at once. Empty fields are filled at night from the complex's flats and the old site, filled ones stay.
const GROUPS = [
  {
    title: 'Основное',
    fields: [
      {key: 'name', label: 'Название', required: true},
      {key: 'sameType', label: 'Варианты написания', extra: 'Через запятую, например: Сетл Ривьера, Setl Riviera. По ним ЖК находится в поиске портала'},
      {key: 'developer', label: 'Застройщик', options: 'builders'},
      {key: 'curatorId', label: 'Куратор', kind: 'curator', extra: 'Его видят агенты; получают квартиры ЖК без своего куратора'},
      {key: 'deadline', label: 'Срок сдачи', options: 'deadline'},
      {key: 'startOfSales', label: 'Старт продаж', kind: 'date'},
    ],
  },
  {
    title: 'Адрес',
    fields: [
      {key: 'address', label: 'Адрес', wide: true},
      {key: 'metroName', label: 'Метро', options: 'metroName'},
      {key: 'distanceToMetro', label: 'До метро', options: 'distanceToMetro'},
      {key: 'latitude', label: 'Широта', placeholder: '59.93863'},
      {key: 'longitude', label: 'Долгота', placeholder: '30.31413'},
    ],
  },
  {
    title: 'Описание',
    fields: [
      {key: 'descriptionIsShort', label: 'Кратко', kind: 'textarea', rows: 3, wide: true, extra: 'В карточке ЖК в списке «Вид ЖК»'},
      {key: 'descriptionComplete', label: 'Полностью', kind: 'textarea', rows: 10, wide: true, extra: 'Блок «Об объекте» на странице ЖК'},
    ],
  },
  {
    title: 'Характеристики',
    fields: [
      {key: 'houseType', label: 'Тип дома', options: 'houseType'},
      {key: 'facade', label: 'Фасад'},
      {key: 'elevator', label: 'Лифт', options: 'elevator'},
      {key: 'agreement', label: 'Договор', options: 'agreement'},
      {key: 'escrow', label: 'Эскроу', options: 'escrow'},
      {key: 'finishing', label: 'Отделка', kind: 'tags', options: 'finishing'},
      {key: 'parking', label: 'Паркинг', kind: 'tags', options: 'parking'},
      {key: 'payment', label: 'Оплата', kind: 'tags', options: 'payment'},
      {key: 'keyFeatures', label: 'Класс и преимущества', kind: 'tags', wide: true, extra: 'Например «Комфорт-класс», «Своя набережная»; Enter добавляет'},
    ],
  },
]
const FORM_FIELDS = GROUPS.flatMap((group) => group.fields)
const LIST_FIELDS = FORM_FIELDS.filter((f) => f.kind === 'tags').map((f) => f.key)

const IMAGES = [
  {key: 'complexImage', label: 'Главное фото', extra: 'Карточка ЖК и верх его страницы'},
  {key: 'layoutImage', label: 'План ЖК', extra: 'Кнопка «План ЖК» на странице'},
]
const DOCUMENTS = [
  {key: 'regulation', label: 'Регламент'},
  {key: 'bookingRegulation', label: 'Регламент бронирования'},
  {key: 'document', label: 'Документ'},
  {key: 'standard', label: 'Стандарт'},
  {key: 'contract', label: 'Договор'},
  {key: 'conditions', label: 'Условия'},
]
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.jfif'
const DOCUMENT_ACCEPT = `${IMAGE_ACCEPT},.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.rtf,.txt,.zip`

const mediaUrl = (file) => `${config.API_HOST}/media/${String(file).split('/').map(encodeURIComponent).join('/')}`
const fileName = (file) => String(file).split('/').pop()

// the card's value as the form holds it, and back
const toForm = (card) => Object.fromEntries(FORM_FIELDS.map(({key}) => {
  const value = card ? card[key] : null
  if (LIST_FIELDS.includes(key)) return [key, Array.isArray(value) ? value : []]
  if (key === 'curatorId') return [key, value || undefined]
  return [key, value === null || value === undefined ? '' : String(value)]
}))
const fromForm = (key, value) => {
  if (LIST_FIELDS.includes(key)) {
    const list = (value || []).map((v) => String(v).trim()).filter(Boolean)
    return list.length ? list : null
  }
  if (key === 'curatorId') return value || null
  const text = String(value === null || value === undefined ? '' : value).trim()
  return text || null
}
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)

// what differs from the saved card
const changesOf = (values, card) => {
  const saved = toForm(card)
  const changes = {}
  FORM_FIELDS.forEach(({key}) => {
    if (!(key in values)) return
    const next = fromForm(key, values[key])
    if (!same(next, fromForm(key, saved[key]))) changes[key] = next
  })
  return changes
}

const errorOf = (error) => error?.response?.data?.errors || {}

// curators, developers and the portal's value lists, loaded once
let referencesPromise = null
const loadReferences = () => {
  if (!referencesPromise) {
    referencesPromise = Promise.all([
      ApiCuratorService.fetchCurators(),
      ApiApartmentBuilderService.fetchBuilders(),
      ApiContentService.fetchContents(),
    ]).then(([curators, builders, contents]) => ({
      curators: (curators.data || []).map((c) => ({value: c.id, label: [c.lastName, c.name, c.patronymic].filter(Boolean).join(' ')})),
      builders: [...new Set((builders.data || []).map((b) => b.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ru')),
      lists: Object.fromEntries((contents.data || []).map((c) => [c.name, (Array.isArray(c.value) ? c.value : []).map((v) => v.value).filter(Boolean)])),
    })).catch((error) => {
      referencesPromise = null
      throw error
    })
  }
  return referencesPromise
}

const optionsOf = (references, name) => (name === 'builders' ? references.builders : references.lists[name] || [])

const Field = ({field, references, disabled}) => {
  const {key, label, kind, options, required, extra, placeholder, rows} = field
  let control
  if (kind === 'textarea') control = <Input.TextArea rows={rows} disabled={disabled} showCount maxLength={20000}/>
  else if (kind === 'date') control = <Input type='date' disabled={disabled}/>
  else if (kind === 'curator') {
    control = <Select allowClear showSearch optionFilterProp='label' placeholder='Не назначен' options={references.curators} disabled={disabled}/>
  } else if (kind === 'tags') {
    control = (
      <Select
        mode='tags'
        allowClear
        tokenSeparators={[',']}
        placeholder='Выберите или впишите'
        options={optionsOf(references, options).map((value) => ({value}))}
        disabled={disabled}
      />
    )
  } else if (options) {
    control = (
      <AutoComplete
        allowClear
        options={optionsOf(references, options).map((value) => ({value}))}
        filterOption={(input, option) => String(option.value).toLowerCase().includes(String(input).toLowerCase())}
        placeholder={placeholder || 'Выберите или впишите'}
        disabled={disabled}
      />
    )
  } else control = <Input placeholder={placeholder} disabled={disabled} maxLength={255}/>

  return (
    <Col xs={24} md={field.wide ? 24 : 12}>
      <Form.Item
        name={key}
        label={label}
        extra={extra}
        rules={required ? [{required: true, whitespace: true, message: 'Обязательное поле'}] : undefined}
      >
        {control}
      </Form.Item>
    </Col>
  )
}

// one photo or document: what is there now, upload and remove
const FileField = ({field, value, accept, image, disabled, busy, onUpload, onRemove}) => (
  <div className='complex-file'>
    <div className='complex-file__head'>
      <b>{field.label}</b>
      {field.extra && <span className='cell-hint'>{field.extra}</span>}
    </div>
    <div className='complex-file__body'>
      {value
        ? (image
          ? <a href={mediaUrl(value)} target='_blank' rel='noopener noreferrer'><img className='complex-file__image' src={mediaUrl(value)} alt={field.label}/></a>
          : <a href={mediaUrl(value)} target='_blank' rel='noopener noreferrer'><FileOutlined/> {fileName(value)}</a>)
        : <span className='cell-empty'>{image ? 'Нет фото' : 'Нет файла'}</span>}
      <Space wrap>
        <Upload accept={accept} showUploadList={false} disabled={disabled || busy} beforeUpload={(file) => { onUpload(field.key, file); return false }}>
          <Button size='small' icon={<UploadOutlined/>} loading={busy} disabled={disabled}>{value ? 'Заменить' : 'Загрузить'}</Button>
        </Upload>
        {value && (
          <Popconfirm title='Убрать с карточки?' okText='Убрать' cancelText='Нет' onConfirm={() => onRemove(field.key)} disabled={disabled}>
            <Button size='small' danger icon={<DeleteOutlined/>} disabled={disabled || busy}>Убрать</Button>
          </Popconfirm>
        )}
      </Space>
    </div>
  </div>
)

// readOnly: the user's roles give «ЖК: просмотр» — the card opens, nothing can be changed
export const ComplexEditor = observer(({complexId, readOnly, onClose, onSaved, onCreated, onDeleted}) => {
  const [form] = Form.useForm()
  const isNew = complexId === 'new'
  const [card, setCard] = useState(null)
  const [editable, setEditable] = useState(null)
  const [info, setInfo] = useState(null)
  const [references, setReferences] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [busyField, setBusyField] = useState(null)
  const [dirty, setDirty] = useState(false)
  const uploads = useRef(Promise.resolve())

  useEffect(() => {
    let alive = true
    setLoadError(null)
    const cardRequest = isNew ? Promise.resolve(null) : ApiComplexesService.fetchCard(complexId)
    Promise.all([cardRequest, loadReferences()])
      .then(([response, refs]) => {
        if (!alive) return
        setReferences(refs)
        const data = response ? response.data : {card: {}, editable: null, flats: null, eciOldIds: []}
        setCard(data.card)
        setEditable(data.editable)
        setInfo({flats: data.flats, eciOldIds: data.eciOldIds || []})
        form.setFieldsValue(toForm(data.card))
        setDirty(false)
      })
      .catch(() => alive && setLoadError('Не удалось открыть карточку. Обновите страницу.'))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexId])

  const canEdit = (key) => !readOnly && (!editable || editable.includes(key))
  const restricted = !readOnly && editable && FORM_FIELDS.some(({key}) => !editable.includes(key))

  const showError = (error, fallback) => {
    const {message: text, field} = errorOf(error)
    if (field && FORM_FIELDS.some((f) => f.key === field)) form.setFields([{name: field, errors: [text]}])
    message.error(text || fallback)
  }

  const applySaved = (saved) => {
    setCard(saved)
    form.setFieldsValue(toForm(saved))
    setDirty(false)
  }

  const save = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (e) {
      return
    }
    const changes = changesOf(values, isNew ? {} : card)
    if (!isNew && !Object.keys(changes).length) {
      message.info('Изменений нет')
      return
    }
    setSaving(true)
    try {
      if (isNew) {
        const {data} = await ApiComplexesService.createCard(changes)
        message.success('ЖК добавлен. Теперь можно загрузить фото и документы.')
        setDirty(false)
        onCreated(data)
      } else {
        const {data} = await ApiComplexesService.updateCard(card.id, changes)
        applySaved(data)
        message.success('Сохранено')
        onSaved(data)
      }
    } catch (error) {
      showError(error, 'Не удалось сохранить')
    }
    setSaving(false)
  }

  // uploads one after another (several photos at once keep their order)
  const upload = (field, file) => {
    uploads.current = uploads.current.then(async () => {
      setBusyField(field)
      try {
        const {data} = await ApiComplexesService.uploadFile(card.id, field, file)
        setCard(data)
        onSaved(data)
      } catch (error) {
        showError(error, `Не удалось загрузить ${file.name}`)
      }
      setBusyField(null)
    })
  }

  const removeFile = async (field, value = null) => {
    setBusyField(field)
    try {
      const {data} = await ApiComplexesService.updateCard(card.id, {[field]: value})
      setCard(data)
      onSaved(data)
    } catch (error) {
      showError(error, 'Не удалось убрать')
    }
    setBusyField(null)
  }

  const remove = async () => {
    try {
      await ApiComplexesService.deleteCard(card.id)
      message.success('ЖК удалён')
      onDeleted(card.id)
    } catch (error) {
      showError(error, 'Не удалось удалить')
    }
  }

  const close = () => {
    if (!dirty) return onClose()
    Modal.confirm({
      title: 'Закрыть без сохранения?',
      content: 'Изменения в полях не сохранены.',
      okText: 'Закрыть',
      cancelText: 'Вернуться',
      onOk: onClose,
    })
  }

  const {latitude, longitude} = card || {}
  const mapLink = latitude && longitude ? `https://yandex.ru/maps/?pt=${longitude},${latitude}&z=16&l=map` : null
  const otherImages = (card && card.otherImages) || []
  const loaded = Boolean(references && card)

  return (
    <Drawer
      visible
      width={Math.min(900, window.innerWidth)}
      title={isNew ? 'Новый ЖК' : (card?.name || 'ЖК')}
      onClose={close}
      destroyOnClose
      className='complex-editor'
      footer={loaded && (
        <div className='complex-editor__footer'>
          {!readOnly && <Button type='primary' onClick={save} loading={saving}>{isNew ? 'Добавить ЖК' : 'Сохранить'}</Button>}
          <Button onClick={close}>Закрыть</Button>
          <span className='section-toolbar__spacer'/>
          {!isNew && authModel.isAdmin && (
            <Popconfirm
              title='Удалить ЖК? Удалить можно только ЖК без квартир и без связи со старым сайтом.'
              okText='Удалить'
              okButtonProps={{danger: true}}
              cancelText='Нет'
              onConfirm={remove}
            >
              <Button danger icon={<DeleteOutlined/>}>Удалить</Button>
            </Popconfirm>
          )}
        </div>
      )}
    >
      {loadError && <Alert type='error' message={loadError}/>}
      {!loadError && !loaded && <div className='complex-editor__loading'><Spin/></div>}
      {loaded && (
        <>
          {!isNew && info && (
            <div className='complex-editor__info'>
              {info.flats && <span><b>{info.flats.onSale}</b> в продаже из {info.flats.total} квартир</span>}
              {info.eciOldIds.length > 0 && <span>На старом сайте: ЖК № {info.eciOldIds.join(', ')}</span>}
              <a href={`${config.API_HOST}/residential-complexes/${card.id}`} target='_blank' rel='noopener noreferrer'>Открыть на портале</a>
              <Link to={`/admin/checkerboard?complex=${card.id}`}>Шахматка ЖК</Link>
            </div>
          )}
          <Alert
            className='section-note'
            type='info'
            showIcon
            message='Пустые поля ночью дозаполняются из квартир ЖК и со старого сайта. Заполненные здесь не меняются.'
          />
          {restricted && <Alert className='section-note' type='warning' showIcon message='Серые поля вашим ролям менять нельзя.'/>}
          <Form form={form} layout='vertical' onValuesChange={() => setDirty(true)}>
            {GROUPS.map((group) => (
              <section key={group.title} className='complex-editor__group'>
                <h3>{group.title}</h3>
                <Row gutter={16}>
                  {group.fields.map((field) => (
                    <Field key={field.key} field={field} references={references} disabled={!canEdit(field.key)}/>
                  ))}
                </Row>
                {group.title === 'Адрес' && mapLink && (
                  <a className='complex-editor__map' href={mapLink} target='_blank' rel='noopener noreferrer'>
                    <EnvironmentOutlined/> Проверить точку на карте
                  </a>
                )}
              </section>
            ))}
          </Form>

          <section className='complex-editor__group'>
            <h3>Фото</h3>
            {isNew
              ? <p className='cell-hint'>Фото и документы можно добавить сразу после того, как ЖК будет добавлен.</p>
              : (
                <>
                  <div className='complex-files'>
                    {IMAGES.map((field) => (
                      <FileField
                        key={field.key}
                        field={field}
                        value={card[field.key]}
                        accept={IMAGE_ACCEPT}
                        image
                        disabled={!canEdit(field.key)}
                        busy={busyField === field.key}
                        onUpload={upload}
                        onRemove={removeFile}
                      />
                    ))}
                  </div>
                  <div className='complex-file'>
                    <div className='complex-file__head'>
                      <b>Другие фото: {otherImages.length}</b>
                      <span className='cell-hint'>Галерея на странице ЖК; можно выбрать несколько файлов сразу</span>
                    </div>
                    <div className='complex-gallery'>
                      {otherImages.map((image) => (
                        <div key={image} className='complex-gallery__item'>
                          <a href={mediaUrl(image)} target='_blank' rel='noopener noreferrer'><img src={mediaUrl(image)} alt=''/></a>
                          {canEdit('otherImages') && (
                            <Popconfirm title='Убрать фото?' okText='Убрать' cancelText='Нет' onConfirm={() => removeFile('otherImages', otherImages.filter((i) => i !== image))}>
                              <button type='button' className='complex-gallery__remove' aria-label='Убрать фото' disabled={busyField === 'otherImages'}>×</button>
                            </Popconfirm>
                          )}
                        </div>
                      ))}
                      <Upload
                        accept={IMAGE_ACCEPT}
                        multiple
                        showUploadList={false}
                        disabled={!canEdit('otherImages')}
                        beforeUpload={(file) => { upload('otherImages', file); return false }}
                      >
                        <Button icon={<UploadOutlined/>} loading={busyField === 'otherImages'} disabled={!canEdit('otherImages')}>Добавить фото</Button>
                      </Upload>
                    </div>
                  </div>
                </>
              )}
          </section>

          {!isNew && (
            <section className='complex-editor__group'>
              <h3>Документы</h3>
              <p className='cell-hint'>Кнопки на странице ЖК у агентов. PDF, Word, Excel, картинки или архив до 30 МБ.</p>
              <div className='complex-files'>
                {DOCUMENTS.map((field) => (
                  <FileField
                    key={field.key}
                    field={field}
                    value={card[field.key]}
                    accept={DOCUMENT_ACCEPT}
                    disabled={!canEdit(field.key)}
                    busy={busyField === field.key}
                    onUpload={upload}
                    onRemove={removeFile}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </Drawer>
  )
})
